/** @format */

import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  TrashIcon,
  AcademicCapIcon,
  UsersIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useFirestore } from "../hooks/useFirestore";
import useCourseClassrooms from "../hooks/useCourseClassrooms";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

const CourseClassroomManager = ({ courseId, isOpen, onClose }) => {
  const { currentUser, userRole } = useAuth();
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);

  // Get all available classrooms
  const { documents: allClassrooms, loading: classroomsLoading } =
    useFirestore("classrooms");

  // Get classrooms already associated with this course
  const {
    classrooms: courseClassrooms,
    loading: courseClassroomsLoading,
    addClassroomToCourse,
    removeClassroomFromCourse,
    bulkAddClassrooms,
    operationLoading,
  } = useCourseClassrooms(courseId);

  // Filter available classrooms (exclude those already added to the course)
  const availableClassrooms = allClassrooms.filter((classroom) => {
    const isAlreadyAdded = courseClassrooms.some(
      (cc) => cc.classroomId === classroom.id
    );
    const matchesSearch =
      classroom.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classroom.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || classroom.category === filterCategory;

    return !isAlreadyAdded && matchesSearch && matchesCategory;
  });

  // Get unique categories from available classrooms
  const categories = [
    ...new Set(availableClassrooms.map((c) => c.category).filter(Boolean)),
  ];

  const handleAddClassroom = async (classroom) => {
    try {
      const classroomData = {
        classroomId: classroom.id,
        classroomName: classroom.name,
        classroomSubject: classroom.subject,
        classroomCategory: classroom.category,
        addedBy: currentUser?.uid,
        addedByRole: userRole,
      };
      
      console.log("Adding classroom to course with data:", classroomData);
      console.log("Source classroom data:", classroom);
      console.log("Classroom ID type:", typeof classroom.id, classroom.id);
      
      const result = await addClassroomToCourse(classroomData);

      if (result.success) {
        setSelectedClassrooms((prev) =>
          prev.filter((id) => id !== classroom.id)
        );
      }
    } catch (error) {
      console.error("Error adding classroom to course:", error);
    }
  };

  const handleRemoveClassroom = async (classroomId) => {
    if (
      window.confirm(
        "Are you sure you want to remove this classroom from the course?"
      )
    ) {
      try {
        await removeClassroomFromCourse(classroomId);
      } catch (error) {
        console.error("Error removing classroom from course:", error);
      }
    }
  };

  const handleBulkAdd = async () => {
    if (selectedClassrooms.length === 0) {
      toast.error("Please select classrooms first");
      return;
    }

    try {
      const classroomData = selectedClassrooms.map((classroomId) => {
        const classroom = allClassrooms.find((c) => c.id === classroomId);
        console.log("Bulk add - classroom found:", classroom);
        console.log("Bulk add - classroom ID type:", typeof classroom?.id, classroom?.id);
        return {
          classroomId: classroom.id,
          classroomName: classroom.name,
          classroomSubject: classroom.subject,
          classroomCategory: classroom.category,
          addedBy: currentUser?.uid,
          addedByRole: userRole,
        };
      });

      console.log("Bulk adding classrooms to course with data:", classroomData);
      const result = await bulkAddClassrooms(classroomData);
      if (result.success) {
        setSelectedClassrooms([]);
        setShowAddModal(false);
      }
    } catch (error) {
      console.error("Error bulk adding classrooms:", error);
    }
  };

  const handleClassroomSelect = (classroomId) => {
    setSelectedClassrooms((prev) =>
      prev.includes(classroomId)
        ? prev.filter((id) => id !== classroomId)
        : [...prev, classroomId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClassrooms.length === availableClassrooms.length) {
      setSelectedClassrooms([]);
    } else {
      setSelectedClassrooms(availableClassrooms.map((c) => c.id));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AcademicCapIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Manage Course Classrooms
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4">
            {/* Current Course Classrooms */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Current Classrooms ({courseClassrooms.length})
              </h4>
              {courseClassroomsLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : courseClassrooms.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No classrooms added to this course yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {courseClassrooms.map((courseClassroom) => (
                    <div
                      key={courseClassroom.id}
                      className="bg-gray-50 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {courseClassroom.classroomName || "Unknown Classroom"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {courseClassroom.classroomSubject} •{" "}
                          {courseClassroom.classroomCategory}
                        </div>
                        <div className="text-xs text-gray-400">
                          Added{" "}
                          {new Date(
                            courseClassroom.addedAt
                          ).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          handleRemoveClassroom(courseClassroom.id)
                        }
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remove from course"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Classrooms */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900">
                  Add New Classrooms
                </h4>
                <button
                  onClick={() => setShowAddModal(!showAddModal)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {showAddModal ? "Hide" : "Add Classrooms"}
                </button>
              </div>

              {showAddModal && (
                <div className="bg-gray-50 rounded-lg p-4">
                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search classrooms..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="block w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Available Classrooms List */}
                  {classroomsLoading ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    <>
                      {availableClassrooms.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          {searchTerm || filterCategory !== "all"
                            ? "No classrooms match your search criteria."
                            : "All available classrooms are already added to this course."}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  selectedClassrooms.length ===
                                    availableClassrooms.length &&
                                  availableClassrooms.length > 0
                                }
                                onChange={handleSelectAll}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {selectedClassrooms.length} of{" "}
                                {availableClassrooms.length} selected
                              </span>
                            </div>
                            {selectedClassrooms.length > 0 && (
                              <button
                                onClick={handleBulkAdd}
                                disabled={operationLoading}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                              >
                                <PlusIcon className="h-4 w-4 mr-1" />
                                Add Selected ({selectedClassrooms.length})
                              </button>
                            )}
                          </div>

                          <div className="max-h-64 overflow-y-auto space-y-2">
                            {availableClassrooms.map((classroom) => (
                              <div
                                key={classroom.id}
                                className="flex items-center space-x-3 p-3 bg-white rounded border hover:bg-gray-50"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedClassrooms.includes(
                                    classroom.id
                                  )}
                                  onChange={() =>
                                    handleClassroomSelect(classroom.id)
                                  }
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {classroom.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {classroom.subject} • {classroom.category}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {classroom.memberCount || 0} members
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddClassroom(classroom)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Add to course"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseClassroomManager;
